<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  sections: { id: string; label: string }[];
}>();

const { t } = useI18n();

const activeId = ref('');

// Le seuil dépasse le scroll-mt-20 (80 px) des sections : la cible d'un clic, posée exactement
// à 80 px, doit compter comme atteinte malgré les arrondis de défilement.
const reachedThreshold = 96;

let holdUntil = 0;

// La section active est la dernière dont le haut est passé sous la barre.
// Tant qu'aucune section n'est atteinte, c'est la première qui reste active : en haut de page,
// le visiteur lit bien la section d'ouverture.
function measureActive() {
  if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
    return props.sections[props.sections.length - 1]?.id ?? '';
  }

  let current = '';
  for (const section of props.sections) {
    const element = document.getElementById(section.id);
    if (!element) continue;
    if (element.getBoundingClientRect().top <= reachedThreshold) current = section.id;
  }
  return current || (props.sections[0]?.id ?? '');
}

function onScroll() {
  if (performance.now() < holdUntil) return;
  activeId.value = measureActive();
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  activeId.value = measureActive();
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onScroll);
});

// On défile directement vers l'élément plutôt que de passer par une ancre d'URL :
// la page de projet se sert déjà du hash comme état de sélection du modèle.
function goTo(id: string) {
  activeId.value = id;
  holdUntil = performance.now() + 300;
  document.getElementById(id)?.scrollIntoView({ block: 'start' });
}
</script>

<template>
  <nav v-if="sections.length > 1" :aria-label="t('views.project.anchors.label')" class="no-print sticky top-0 z-30 hidden border-b border-gray-200 bg-white py-3 lg:block">
    <ul class="flex flex-wrap gap-0.5 rounded-xl bg-gray-100 p-1">
      <li v-for="section in sections" :key="section.id" class="min-w-fit flex-1">
        <button
          :aria-current="section.id === activeId ? 'location' : undefined"
          :class="section.id === activeId ? 'text-ph-green-700 bg-white shadow-sm' : 'text-gray-600 hover:bg-white/70 hover:text-gray-900'"
          class="focus-visible:outline-ph-green-600 w-full cursor-pointer rounded-lg px-3.5 py-1.5 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2"
          type="button"
          @click="goTo(section.id)"
        >
          {{ section.label }}
        </button>
      </li>
    </ul>
  </nav>
</template>

<style lang="css" scoped></style>
